import { makeAutoObservable, runInAction } from 'mobx';

import { referralsService } from '@/services/referrals';
import { ACTION_STATUS } from '@/types/deal';
import { REFERRAL_STATUS, type Referral, type ReferralAction } from '@/types/referral';

interface DealPivot {
  dealId: number;
  referrals: Referral[];
  /** Referee has at least one Action for this deal but hasn't verified yet. */
  signedUp: number;
  /** Action verified — referrer's Earning is owed but not yet booked. */
  pending: number;
  /** Earning has been booked for this deal × referee. */
  paid: number;
}

class ReferralsStore {
  referrals: Referral[] = [];
  /**
   * Per-deal referral money actually earned, keyed by deal id — the
   * server's SUM of booked Earnings from the referrals index `summary`.
   * Replaces the old `paid count × referrer_cut_cents` client math, which
   * went wrong the moment any bonus (or rate change) made history
   * mixed-rate. Deals with no booked earnings simply have no entry.
   */
  perDealEarnedCents: Map<number, number> = new Map();

  loaded = false;
  refreshing = false;
  refreshError: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** True before the first successful refresh — drives the initial spinner. */
  get isInitialLoading(): boolean {
    return this.refreshing && !this.loaded;
  }

  get totalCount(): number {
    return this.referrals.length;
  }

  get paidCount(): number {
    return this.referrals.filter((r) => r.status === REFERRAL_STATUS.Paid).length;
  }

  get pendingCount(): number {
    // Anything past pending but not yet paid — surfaces "in flight" on UI.
    return this.referrals.filter(
      (r) =>
        r.status === REFERRAL_STATUS.Activated || r.status === REFERRAL_STATUS.Qualified,
    ).length;
  }

  /** "Invited" stage — signed up but no deal click yet. */
  get invitedCount(): number {
    return this.referrals.filter((r) => r.status === REFERRAL_STATUS.Pending).length;
  }

  /** Paid / total. Returns 0 when there are no referrals (avoid NaN in UI). */
  get conversionPct(): number {
    if (this.referrals.length === 0) return 0;
    return Math.round((this.paidCount / this.referrals.length) * 100);
  }

  /**
   * Pivot referrals by deal — drives the Refer tab's per-deal funnel
   * stats. Only referees with an active Action (clicked/pending/verified/paid)
   * for the deal are included; rejected/expired Actions are filtered out
   * so the per-deal list shows the live funnel only. Invariant:
   * `signedUp + pending + paid === referrals.length`.
   */
  byDeal(dealId: number): DealPivot {
    const matched: Referral[] = [];
    let signedUp = 0;
    let pending = 0;
    let paid = 0;

    for (const referral of this.referrals) {
      const action = referral.actions.find((a) => a.deal_id === dealId);
      if (!action) continue;
      const bucket = bucketForAction(action);
      if (bucket === null) continue;
      matched.push(referral);
      if (bucket === 'signedUp') signedUp += 1;
      else if (bucket === 'pending') pending += 1;
      else paid += 1;
    }

    return { dealId, referrals: matched, signedUp, pending, paid };
  }

  /**
   * Per-deal referral money earned — straight from the server's summary
   * (see `perDealEarnedCents`). Returns 0 when the deal has no booked
   * referral earnings, or when the server didn't send sums (older API).
   */
  paidEarningsCentsForDeal(dealId: number): number {
    return this.perDealEarnedCents.get(dealId) ?? 0;
  }

  /**
   * Idempotent loader for tab mount effects. Skips when a refresh is
   * already in flight or when data has already arrived. Components
   * call this in their mount effect; pull-to-refresh should call
   * `refresh()` directly to force a re-fetch.
   */
  ensureLoaded(): Promise<void> {
    if (this.refreshing) return Promise.resolve();
    if (this.loaded) return Promise.resolve();
    return this.refresh().catch(() => {
      // Error captured on store.refreshError; callers don't need to handle.
    });
  }

  async refresh(): Promise<void> {
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
    });
    try {
      const response = await referralsService.list();
      runInAction(() => {
        this.referrals = response.referrals;
        this.perDealEarnedCents = new Map(
          (response.summary?.per_deal ?? []).map((row) => [row.deal_id, row.earned_cents]),
        );
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError =
          err instanceof Error ? err.message : 'Failed to load referrals.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
    }
  }

  reset(): void {
    runInAction(() => {
      this.referrals = [];
      this.perDealEarnedCents = new Map();
      this.loaded = false;
      this.refreshing = false;
      this.refreshError = null;
    });
  }
}

function bucketForAction(action: ReferralAction): 'signedUp' | 'pending' | 'paid' | null {
  switch (action.status) {
    case ACTION_STATUS.Clicked:
    case ACTION_STATUS.Pending:
      return 'signedUp';
    case ACTION_STATUS.Verified:
      return 'pending';
    case ACTION_STATUS.Paid:
      return 'paid';
    case ACTION_STATUS.Rejected:
    case ACTION_STATUS.Expired:
      return null;
  }
}

export const referralsStore = new ReferralsStore();
// `ReferralsStore` class is exported for tests so each test gets a fresh
// instance. Production code should use the `referralsStore` singleton.
export { ReferralsStore };
